import * as React from "react";
import { useIntl, FormattedMessage, FormattedDate } from "react-intl";
import { NaiveDate } from "@/utils/date";
import { Duration } from "@/utils/duration";
import { Time } from "@/utils/time";
import { Integer, Float, Decimal, formatNum } from "@/utils/number";
import { useBufferFileType } from "@/utils/fileType";
import { encodeBytesToBase64 } from "@/utils/base64ArrayBuffer";
import { TypedNull } from "@/utils/typed_null";
import { FormattedDataSize } from "@/components/FormattedDataSize";
import { MinusSquareIcon, PlusSquareIcon } from "@/icons";
import { cn } from "@/utils/tailwind";

interface JsonSettings {
  showType: "all" | "primitive" | "none";
  expanded: boolean;
  showNumItems: boolean;
  longDates: boolean;
  className: string;
  truncateString: number;
  maxLineLength: number;
  rawString: boolean;
  overrides: Partial<JsonSettings>[];
}

const DEFAULT_SETTINGS: JsonSettings = {
  showType: "all",
  expanded: false,
  longDates: true,
  showNumItems: true,
  className: "",
  truncateString: 30,
  maxLineLength: 60,
  rawString: false,
  overrides: [],
};

const INDENT = "pl-4";

type JsonValue =
  | null
  | boolean
  | number
  | string
  | TypedNull
  | Integer
  | Float
  | Decimal
  | Date
  | NaiveDate
  | Time
  | Duration
  | Uint8Array
  | Record<string, unknown>
  | Array<unknown>;

enum JsonValueType {
  Boolean = "Boolean",
  String = "String",
  Binary = "Binary",
  Integer = "Integer",
  Float = "Float",
  Decimal = "Decimal",
  Timestamp = "Timestamp",
  Time = "Time",
  Date = "Date",
  Duration = "Duration",
  Struct = "Struct",
  List = "List",
}

interface JsonProps {
  keyName?: string;
  value: unknown;
  className?: string;
  settings?: Partial<JsonSettings>[];
}

const overrideSettings = (
  settings: JsonSettings,
  override?: Partial<JsonSettings>,
): JsonSettings => {
  const newSettings = Object.assign({}, settings);
  return override
    ? Object.assign(
        newSettings,
        Object.fromEntries(
          Object.entries(override).filter(
            ([_key, value]) => value !== "undefined",
          ),
        ),
      )
    : newSettings;
};

const settingsNextLayer = (settings: JsonSettings) => {
  const [override, ...overrides] = settings.overrides;
  const newSettings = overrideSettings(settings, override);
  newSettings.overrides = overrides;
  return newSettings;
};

const nullType = (value: TypedNull | null): JsonValueType | null => {
  if (value === null) {
    return null;
  }
  switch (value.ty.name) {
    case Boolean.name:
      return JsonValueType.Boolean;
    case Integer.name:
      return JsonValueType.Integer;
    case String.name:
      return JsonValueType.String;
    case Uint8Array.name:
      return JsonValueType.Binary;
    case Float.name:
    case Number.name:
      return JsonValueType.Float;
    case Decimal.name:
      return JsonValueType.Decimal;
    case Date.name:
      return JsonValueType.Timestamp;
    case NaiveDate.name:
      return JsonValueType.Date;
    case Time.name:
      return JsonValueType.Time;
    case Duration.name:
      return JsonValueType.Duration;
    case Object.name:
      return JsonValueType.Struct;
    case Array.name:
      return JsonValueType.List;
    default:
      return null;
  }
};

type JsonValueProps<T> = {
  value: T;
  settings: JsonSettings;
};

const jsonValue = ({ value, settings }: JsonValueProps<unknown>) => {
  if (value === null) {
    return <NullValue value={value} settings={settings} />;
  }
  if (typeof value === "number") {
    if (value % 1 === 0) {
      return <IntegerValue value={value} settings={settings} />;
    } else {
      return <FloatValue value={value} settings={settings} />;
    }
  }
  if (typeof value === "string") {
    return <StringValue value={value} settings={settings} />;
  }
  if (typeof value === "boolean") {
    return <BooleanValue value={value} settings={settings} />;
  }
  if (value instanceof TypedNull) {
    return <NullValue value={value} settings={settings} />;
  }
  if (value instanceof Integer) {
    return <IntegerValue value={value} settings={settings} />;
  }
  if (value instanceof Float) {
    return <FloatValue value={value} settings={settings} />;
  }
  if (value instanceof Decimal) {
    return <DecimalValue value={value} settings={settings} />;
  }
  if (value instanceof Date) {
    return <TimestampValue value={value} settings={settings} />;
  }
  if (value instanceof NaiveDate) {
    return <DateValue value={value} settings={settings} />;
  }
  if (value instanceof Time) {
    return <TimeValue value={value} settings={settings} />;
  }
  if (value instanceof Duration) {
    return <DurationValue value={value} settings={settings} />;
  }
  if (value instanceof Uint8Array) {
    return <BinaryValue value={value} settings={settings} />;
  }
  if (value instanceof Array) {
    return <ListValue value={value} settings={settings} />;
  }
  if (
    value instanceof Object &&
    Object.keys(value).every((key) => typeof key === "string")
  ) {
    return (
      <StructValue
        value={value as Record<string, unknown>}
        settings={settings}
      />
    );
  }
  return <UnknownValue value={value} settings={settings} />;
};

const ExpandIcon = ({
  expanded,
  setExpanded,
}: {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}) => {
  const className = "hover:cursor-pointer size-4 inline -mt-1";
  const onClick = () => setExpanded(!expanded);
  const Component = expanded ? MinusSquareIcon : PlusSquareIcon;
  return (
    <span>
      <Component onClick={onClick} className={className} />{" "}
    </span>
  );
};

const Key = ({ name }: { name: string }) => {
  return <span className="text-blue-500 font-semibold pr-2">{name}:</span>;
};

const TypeName = ({
  ty,
  settings,
}: {
  ty: JsonValueType;
  settings: JsonSettings;
}) => {
  const { showType } = settings;
  switch (showType) {
    case "none":
      return null;
    case "primitive":
      if (ty == JsonValueType.List || ty == JsonValueType.Struct) {
        return null;
      }
  }
  return <span className="lowercase text-xs font-normal">{ty} </span>;
};

const NullValue = ({ value, settings }: JsonValueProps<TypedNull | null>) => {
  const ty = nullType(value);
  return (
    <span className="whitespace-nowrap text-grey-500 font-semibold">
      {ty && <TypeName settings={settings} ty={ty} />}
      <FormattedMessage defaultMessage="Null" />
    </span>
  );
};

const BooleanValue = ({ value, settings }: JsonValueProps<boolean>) => {
  return (
    <span className="whitespace-nowrap text-red-500 font-semibold">
      <TypeName settings={settings} ty={JsonValueType.Boolean} />
      {value ? (
        <FormattedMessage defaultMessage="true" />
      ) : (
        <FormattedMessage defaultMessage="false" />
      )}
    </span>
  );
};

const StringValue = ({ value, settings }: JsonValueProps<string>) => {
  const {
    rawString,
    expanded: defaultExpanded,
    truncateString,
    maxLineLength,
    showNumItems,
  } = settings;
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  let string = rawString ? value : JSON.stringify(value).slice(1, -1);
  let truncated = false;
  const truncateLength = Math.max(
    0,
    truncateString - `... ${value.length} chars`.length,
  );
  if (!expanded && string.length > truncateString) {
    string = `${string.slice(0, truncateLength)}...`;
    truncated = true;
  }
  const quote = rawString ? "" : '"';
  if (string.length < maxLineLength) {
    return (
      <span className="whitespace-nowrap">
        {(truncated || expanded) && (
          <ExpandIcon expanded={expanded} setExpanded={setExpanded} />
        )}
        <TypeName settings={settings} ty={JsonValueType.String} />
        {quote}
        {string}
        {quote}
        {truncated && showNumItems && (
          <span className="text-grey-500 text-xs">
            <FormattedMessage
              defaultMessage=" {chars, plural,
                one {# char}
                other {# chars}
              }"
              values={{ chars: value.length }}
            />
          </span>
        )}
      </span>
    );
  } else {
    return (
      <>
        <span>
          <ExpandIcon expanded={expanded} setExpanded={setExpanded} />
          <TypeName settings={settings} ty={JsonValueType.String} />
          {quote}
        </span>
        <div style={{ width: `${maxLineLength}ch` }} className={cn(INDENT)}>
          {string}
        </div>
        <span>{quote}</span>
      </>
    );
  }
};

const IntegerValue = ({
  value,
  settings,
}: JsonValueProps<Integer | number>) => {
  const intl = useIntl();
  return (
    <span className="whitespace-nowrap text-orange-500 font-semibold">
      <TypeName settings={settings} ty={JsonValueType.Integer} />
      {formatNum(value, intl.locale)}
    </span>
  );
};

const FloatValue = ({ value, settings }: JsonValueProps<Float | number>) => {
  const intl = useIntl();
  return (
    <span className="whitespace-nowrap text-orange-500 font-semibold">
      <TypeName settings={settings} ty={JsonValueType.Float} />
      {formatNum(value, intl.locale)}
    </span>
  );
};

const DecimalValue = ({
  value,
  settings,
}: JsonValueProps<Decimal | number>) => {
  const intl = useIntl();
  return (
    <span className="whitespace-nowrap text-orange-500 font-semibold">
      <TypeName settings={settings} ty={JsonValueType.Decimal} />
      {formatNum(value, intl.locale)}
    </span>
  );
};

const TimestampValue = ({ value, settings }: JsonValueProps<Date>) => {
  const { longDates } = settings;
  return (
    <span
      className="whitespace-nowrap text-violet-500 font-semibold"
      // necessary because of timezones
      suppressHydrationWarning
    >
      <TypeName settings={settings} ty={JsonValueType.Timestamp} />
      {longDates ? (
        <FormattedDate
          value={value}
          year="numeric"
          month="short"
          day="2-digit"
          hour="2-digit"
          minute="2-digit"
          second="2-digit"
        />
      ) : (
        value.toISOString()
      )}
    </span>
  );
};

const TimeValue = ({ value, settings }: JsonValueProps<Time>) => {
  const { longDates } = settings;
  return (
    <span className="whitespace-nowrap text-violet-500 font-semibold">
      <TypeName settings={settings} ty={JsonValueType.Time} />
      {longDates ? (
        <FormattedDate
          value={value.toDate()}
          hour="2-digit"
          minute="2-digit"
          second="2-digit"
        />
      ) : (
        value.toString()
      )}
    </span>
  );
};

const DateValue = ({ value, settings }: JsonValueProps<NaiveDate>) => {
  const { longDates } = settings;
  return (
    <span className="whitespace-nowrap text-violet-500 font-semibold">
      <TypeName settings={settings} ty={JsonValueType.Date} />
      {longDates ? (
        <FormattedDate
          value={value.toLocale()}
          year="numeric"
          month="short"
          day="2-digit"
        />
      ) : (
        value.toString()
      )}
    </span>
  );
};

const DurationValue = ({ value, settings }: JsonValueProps<Duration>) => {
  const intl = useIntl();
  const { longDates } = settings;
  return (
    <span className="whitespace-nowrap text-violet-500 font-semibold">
      <TypeName settings={settings} ty={JsonValueType.Duration} />
      {longDates ? value.format(intl.locale) : value.toString()}
    </span>
  );
};

const BinaryDisplay = ({
  buffer,
  mimeType,
}: {
  buffer: Uint8Array;
  mimeType: string;
}) => {
  const start = mimeType.split("/")[0];
  if (!["image", "audio", "video"].includes(start)) {
    return null;
  }
  const src = `data:${mimeType};base64,${encodeBytesToBase64(buffer)}`;
  switch (start) {
    case "image":
      return <img src={src} className="w-full" />;
    case "audio":
      return <audio src={src} className="w-full" />;
    case "video":
      return <video src={src} className="w-full" />;
    default:
      return null;
  }
};

const BinaryValue = ({ value, settings }: JsonValueProps<Uint8Array>) => {
  const { expanded: defaultExpanded, showNumItems, maxLineLength } = settings;
  const mimeType = useBufferFileType(value)?.mime || "application/octet-stream";
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const display = React.useMemo(
    () => BinaryDisplay({ buffer: value, mimeType }),
    [value, mimeType],
  );
  return expanded ? (
    <>
      <span className="whitespace-nowrap text-emerald-500 font-semibold">
        <ExpandIcon expanded={expanded} setExpanded={setExpanded} />
        <TypeName settings={settings} ty={JsonValueType.Binary} />
        {mimeType}
      </span>
      <div
        style={{
          width: `${maxLineLength}ch`,
        }}
      >
        {display}
      </div>
    </>
  ) : (
    <span className="whitespace-nowrap text-emerald-500 font-semibold">
      {display != null && (
        <ExpandIcon expanded={expanded} setExpanded={setExpanded} />
      )}
      <TypeName settings={settings} ty={JsonValueType.Binary} />
      {mimeType}
      {showNumItems && (
        <span className="text-grey-500 text-xs">
          {" "}
          <FormattedDataSize bytes={value.length} />
        </span>
      )}
    </span>
  );
};

const StructValue = ({
  value,
  settings,
}: JsonValueProps<Record<string, unknown>>) => {
  const { expanded: defaultExpanded, showNumItems } = settings;
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const open = <span className="font-semibold">{"{"}</span>;
  const close = <span className="font-semibold">{"}"}</span>;
  return expanded ? (
    <>
      <span className="whitespace-nowrap">
        <ExpandIcon expanded={expanded} setExpanded={setExpanded} />
        <TypeName settings={settings} ty={JsonValueType.Struct} />
        {open}
      </span>
      <div className={cn(INDENT)}>
        {Object.entries(value).map(([key, value]) => (
          <JsonValue
            key={key}
            keyName={key}
            value={value}
            settings={settings}
          />
        ))}
      </div>
      <span>{close}</span>
    </>
  ) : (
    <span className="whitespace-nowrap">
      <ExpandIcon expanded={expanded} setExpanded={setExpanded} />
      <TypeName settings={settings} ty={JsonValueType.Struct} />
      {open}
      {showNumItems && (
        <span className="text-grey-500 text-xs">
          <FormattedMessage
            defaultMessage=" {items, plural,
                one {# item}
                other {# items}
              }"
            values={{ items: Object.keys(value).length }}
          />
        </span>
      )}
      {close}
    </span>
  );
};

const ListValue = ({ value, settings }: JsonValueProps<Array<unknown>>) => {
  const { expanded: defaultExpanded, showNumItems } = settings;
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const open = <span className="font-semibold">{"["}</span>;
  const close = <span className="font-semibold">{"]"}</span>;
  return expanded ? (
    <>
      <span className="whitespace-nowrap">
        <ExpandIcon expanded={expanded} setExpanded={setExpanded} />
        <TypeName settings={settings} ty={JsonValueType.List} />
        {open}
      </span>
      <div className={cn(INDENT)}>
        {value.map((value, index) => (
          <JsonValue key={index} value={value} settings={settings} />
        ))}
      </div>
      <span>{close}</span>
    </>
  ) : (
    <span className="whitespace-nowrap">
      <ExpandIcon expanded={expanded} setExpanded={setExpanded} />
      <TypeName settings={settings} ty={JsonValueType.List} />
      {open}
      {showNumItems && (
        <span className="text-grey-500 text-xs">
          <FormattedMessage
            defaultMessage=" {items, plural,
                one {# item}
                other {# items}
              }"
            values={{ items: value.length }}
          />
        </span>
      )}
      {close}
    </span>
  );
};

const UnknownValue = ({ value }: JsonValueProps<unknown>) => {
  return <span className="text-grey-500">{JSON.stringify(value)}</span>;
};

const JsonValue = ({
  keyName,
  value,
  settings: baseSettings,
}: {
  keyName?: string;
  value: unknown;
  settings: JsonSettings;
}) => {
  const settings = settingsNextLayer(baseSettings);
  const rendered = jsonValue({ value, settings });
  return (
    <div className={settings.className}>
      {keyName && <Key name={keyName} />}
      {rendered}
    </div>
  );
};

export const Json = ({
  keyName,
  value,
  settings: overrides,
  className,
}: JsonProps) => {
  const settings = React.useMemo(
    () =>
      overrides
        ? overrideSettings(DEFAULT_SETTINGS, { overrides })
        : DEFAULT_SETTINGS,
    [overrides],
  );
  return (
    <div className={className}>
      <JsonValue keyName={keyName} value={value} settings={settings} />
    </div>
  );
};
